import React, { useState, useEffect, useContext, useMemo } from "react";
import { LocalesProvider, LocalesContext } from "./contexts/LocalesContext";
import SectionContentComponent from "./components/SectionContentComponent";
import SectionContentBlockComponent from "./components/SectionContentBlockComponent";
import IrriappResumeComponent from "./irriapp/components/ConfResumeComponent";
import MiplusResumeComponent from "./miplus/components/ConfResumeComponent";
import Miplus2ResumeComponent from "./miplus2/components/ConfResumeComponent";
import { getMachineParamsValue } from "./utils/irriapp";

function SessionPresets(props) {
  const { session } = props;
  const unitsSystem = props.units_system;
  const deviceType = props.session_type || 'irriapp';
  const dataJsonUrl = props.data_json_url;
  
  const [miplus2Data, setMiplus2Data] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const {
    t,
    translations,
    actions: localesActions,
  } = useContext(LocalesContext);

  // MEMOS ******************************************************************

  const configuration = useMemo(() => {
    if (deviceType === 'irriapp') {
      const c = {};
      Object.keys(props.session.params).forEach((key) => {
        if (!key.startsWith("program_")) return;
        c[key.replace("program_", "")] = getMachineParamsValue(
          props.session.params,
          key
        );
      });
      return c;

    } else if (deviceType === 'miplus') {
      return props.session?.params?.logPreset;

    } else if (deviceType === 'miplus2') {
      console.log('[SessionPresets] building configuration from miplus2Data=', miplus2Data);
      if (miplus2Data?.presets) {
        const firstDevice = Object.values(miplus2Data.presets)[0];
        console.log('[SessionPresets] firstDevice=', firstDevice);
        if (firstDevice) {
          if (firstDevice.newJson?.preset) {
            console.log('[SessionPresets] using newJson.preset path');
            const result = {
              ...firstDevice.newJson.preset,
              meta: firstDevice.meta || {}
            };
            console.log('[SessionPresets] configuration=', result);
            return result;
          } else {
            console.log('[SessionPresets] using raw b1/b2/map path');
            const firstMapEntry = firstDevice.map ? Object.values(firstDevice.map)[0] : {};
            console.log('[SessionPresets] firstMapEntry=', firstMapEntry);
            const result = {
              b1: firstDevice.b1 || {},
              b2: firstDevice.b2 || {},
              masks: {
                b1: [firstMapEntry['stWorkConf_B1-wWorkingRowMask'] ?? 0],
                b2: [firstMapEntry['stWorkConf_B2-wWorkingRowMask'] ?? 0]
              },
              al_cnf: {
                p_th_max: firstMapEntry['stAlarmsConf-wPopulationThresholdMax'] ?? 0,
                p_th_min: firstMapEntry['stAlarmsConf-wPopulationThresholdMin'] ?? 0,
                mbar_min: firstMapEntry['stAlarmsConf-wVacuumMbarMin'] ?? 0,
                mbar_max: firstMapEntry['stAlarmsConf-wVacuumMbarMax'] ?? 0,
                sd_q_th: firstMapEntry['stAlarmsConf-wSeedQualityThreshold'] ?? 0
              },
              meta: {}
            };
            console.log('[SessionPresets] configuration=', result);
            return result;
          }
        }
      }
      console.warn('[SessionPresets] miplus2Data has no presets key, returning {}');
      return {};
    }
    return {};
  }, [props.session, deviceType, miplus2Data]);

  // EFFECTS ******************************************************************

  useEffect(() => {
    localesActions.setLocale(props.locale);
    localesActions.setTranslations(props.translations);
  }, []);

  useEffect(() => {
    if (deviceType !== 'miplus2') return;
    console.log('[SessionPresets] deviceType=miplus2, miplus2Data=', miplus2Data);
    console.log('[SessionPresets] props.session.params=', props.session?.params);
    console.log('[SessionPresets] dataJsonUrl=', dataJsonUrl);
    if (!miplus2Data) {
      if (props.session?.params?.presets) {
        console.log('[SessionPresets] using session.params.presets');
        setMiplus2Data(props.session.params);
      } else if (dataJsonUrl) {
        console.log('[SessionPresets] fetching dataJsonUrl...');
        setLoading(true);
        fetch(dataJsonUrl)
          .then(response => response.json())
          .then(data => {
            console.log('[SessionPresets] fetch OK, data=', data);
            setMiplus2Data(data);
            setLoading(false);
          })
          .catch(error => {
            console.error('[SessionPresets] fetch error:', error);
            setLoading(false);
          });
      } else {
        console.warn('[SessionPresets] no params.presets and no dataJsonUrl — cannot load preset data');
      }
    }
  }, [deviceType, dataJsonUrl, miplus2Data]);

  // RENDER ******************************************************************

  if (deviceType === 'miplus2') {
    if (loading) {
      return (
        <SectionContentComponent>
          <SectionContentBlockComponent sizes={{ default: 24 }}>
              <div className="c-message">
                <div className="c-message__content c-message__content--small">
                  Caricamento dati preset...
                </div>
              </div>
          </SectionContentBlockComponent>
        </SectionContentComponent>
      );
    }

    if (!miplus2Data) {
      return (
        <SectionContentComponent>
          <SectionContentBlockComponent sizes={{ default: 24 }}>
              <div className="c-message">
                <div className="c-message__content c-message__content--small">
                  Dati preset non disponibili
                </div>
              </div>
          </SectionContentBlockComponent>
        </SectionContentComponent>
      );
    }

    return (
        <SectionContentComponent>
          <SectionContentBlockComponent sizes={{ default: 24, md: 10 }}>
              <Miplus2ResumeComponent 
                configuration={configuration} 
                unitsSystem={unitsSystem}
              />
          </SectionContentBlockComponent>
        </SectionContentComponent>
    );
  }

  if (deviceType === 'miplus') {
    return (
      <SectionContentComponent>
        <SectionContentBlockComponent sizes={{ default: 24, md: 10 }}>
            <MiplusResumeComponent 
              configuration={configuration} 
              unitsSystem={unitsSystem}
            />
        </SectionContentBlockComponent>
      </SectionContentComponent>
    );
  }

  return (
    <SectionContentComponent>
      <SectionContentBlockComponent sizes={{ default: 24, md: 10 }}>
          <IrriappResumeComponent 
            configuration={configuration} 
            unitsSystem={unitsSystem}
          />
      </SectionContentBlockComponent>
    </SectionContentComponent>
  );
}

export default (props) => {
  return (
    <LocalesProvider>
      <SessionPresets {...props} />
    </LocalesProvider>
  );
};